/**
 * Request schema utilities.
 *
 * Canonical app-layer contract uses v2 fields:
 * summary/details/category/triage_owner_id/requester_person_id.
 */

import type { Database } from '@/integrations/supabase/types';
import { toDbRequestCategory } from "@/lib/request-category-compat";

export type DbRequestInsert = Database['public']['Tables']['requests']['Insert'];
type DbVisibility = Database['public']['Enums']['visibility_level'];

export interface CanonicalRequestInput {
  organizationId: string;
  triageOwnerId: string | null;
  summary: string;
  category: string;
  careType?: string | null;
  details?: string | null;
  requesterPersonId?: string | null;
  priority?: string;
  visibility?: string;
  channel?: string;
  dueAt?: string | null;
}

export function toDbVisibility(visibility: string | undefined): DbVisibility {
  if (visibility === 'private' || visibility === 'restricted' || visibility === 'normal') {
    return visibility;
  }
  return 'normal';
}

/**
 * Canonical app-layer payload using v2 request fields.
 */
export function buildCanonicalRequestInsert(input: CanonicalRequestInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    organization_id: input.organizationId,
    triage_owner_id: input.triageOwnerId,
    summary: input.summary,
    category: toDbRequestCategory(input.category, { careType: input.careType }),
    details: input.details ?? null,
    priority: input.priority ?? 'normal',
    visibility: toDbVisibility(input.visibility),
    status: 'open',
    channel: input.channel ?? 'staff',
    requester_person_id: input.requesterPersonId ?? null,
  };

  if (input.dueAt) payload.due_at = input.dueAt;
  return payload;
}

const KNOWN_CANONICAL_COLUMNS = new Set<string>([
  'organization_id',
  'triage_owner_id',
  'summary',
  'category',
  'details',
  'priority',
  'visibility',
  'status',
  'channel',
  'requester_person_id',
  'due_at',
]);

export function validateCanonicalInsertPayload(payload: Record<string, unknown>): string[] {
  return Object.keys(payload).filter((key) => !KNOWN_CANONICAL_COLUMNS.has(key));
}
